<template>
  <div class="manage-subhires-modal">
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">
        Manage Subhires
      </h3>
      <p class="text-sm text-surface-600 dark:text-surface-400">
        Equipment List Line: <strong>{{ equipmentListLineName }}</strong>
      </p>
      <p class="text-sm text-surface-600 dark:text-surface-400">
        Available Quantity: <strong>{{ requiredQuantity }}</strong>
      </p>
    </div>

    <!-- Quantity to Subhire -->
    <div class="mb-6">
      <label class="block text-sm font-medium mb-2">Quantity to Subhire</label>
      <InputNumber
        v-model="quantityToSubhire"
        :min="1"
        :max="requiredQuantity"
        show-buttons
        button-layout="horizontal"
        class="w-full"
        input-class="w-full text-center"
        decrement-button-class="p-button-secondary"
        increment-button-class="p-button-secondary"
        increment-button-icon="pi pi-plus"
        decrement-button-icon="pi pi-minus"
      />
      <p
        v-if="quantityToSubhire > requiredQuantity"
        class="text-red-500 text-xs mt-1"
      >
        Quantity cannot exceed available quantity
      </p>
    </div>

    <!-- Existing Subhires for this Line Item -->
    <div
      v-if="existingSubhiresForLine.length > 0"
      class="mb-6"
    >
      <label class="block text-sm font-medium mb-2">Already Subhired</label>
      <div class="border border-surface-200 dark:border-surface-700 rounded-lg overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-surface-100 dark:bg-surface-800">
            <tr>
              <th class="px-3 py-2 text-left font-medium">
                Qty
              </th>
              <th class="px-3 py-2 text-left font-medium">
                Subhire #
              </th>
              <th class="px-3 py-2 text-left font-medium">
                Supplier
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in existingSubhiresForLine"
              :key="item.subhireId"
              class="border-t border-surface-200 dark:border-surface-700"
            >
              <td class="px-3 py-2">
                {{ item.quantity }}
              </td>
              <td class="px-3 py-2 font-medium">
                {{ item.subhireNumber }}
              </td>
              <td class="px-3 py-2 text-surface-600 dark:text-surface-400">
                {{ item.supplierName }}
              </td>
            </tr>
          </tbody>
          <tfoot class="bg-surface-50 dark:bg-surface-800/50">
            <tr class="border-t border-surface-200 dark:border-surface-700">
              <td class="px-3 py-2 font-semibold">
                {{ totalSubhiredQuantity }}
              </td>
              <td
                colspan="2"
                class="px-3 py-2 text-surface-500 text-xs"
              >
                Total subhired
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Select Existing Subhire or Create New -->
    <div class="mb-6 space-y-4">
      <div>
        <label class="block text-sm font-medium mb-2">Select Existing Subhire</label>
        <Select
          v-model="selectedExistingSubhireId"
          :options="allSubhiresOptions"
          option-label="label"
          option-value="value"
          placeholder="Choose a subhire..."
          class="w-full"
          :clearable="true"
        >
          <template #option="slotProps">
            <div class="flex items-center justify-between w-full">
              <div>
                <span class="font-medium">{{ slotProps.option.name || slotProps.option.subhire_number || `SH-${slotProps.option.id}` }}</span>
                <span class="text-surface-500 ml-2">
                  - {{ slotProps.option.supplier?.company_name || slotProps.option.supplier?.name || 'Unknown Supplier' }}
                </span>
              </div>
              <Badge
                :value="slotProps.option.status"
                :severity="getStatusSeverity(slotProps.option.status)"
                class="ml-2"
              />
            </div>
          </template>
        </Select>
      </div>
      
      <div class="space-y-3">
        <div>
          <label class="block text-sm font-medium mb-2">Name for new subhire (optional)</label>
          <InputText
            v-model="newSubhireName"
            placeholder="e.g. Lighting rig – Main stage"
            class="w-full"
          />
        </div>
        <div class="flex justify-end gap-2">
          <Button
            label="Create New Subhire"
            icon="pi pi-plus"
            severity="secondary"
            :loading="isCreatingNew"
            @click="createNewSubhire"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { projectStore } from '../projectStore';
import { useToastService } from '@/composables/useToastService';
import {
  addSubHireLine,
  createEquipmentListSubHire,
  getEquipmentListSubHires,
  updateSubHireLine
} from '@/services/subhireEquipmentListService';

const props = defineProps({
  equipmentListLineId: {
    type: [String, Number],
    required: true
  },
  equipmentListLineName: {
    type: String,
    default: 'Unknown Item'
  },
  requiredQuantity: {
    type: Number,
    default: 0
  },
  availableSubhires: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['close']);

const toast = useToastService();

const selectedExistingSubhireId = ref(null);
const isAddingToExisting = ref(false);
const isCreatingNew = ref(false);
const quantityToSubhire = ref(props.requiredQuantity || 1);
const newSubhireName = ref('');

const allSubhiresOptions = computed(() => {
  // Use availableSubhires from props if provided, otherwise use loaded subhires
  const subhiresList = props.availableSubhires.length > 0 ? props.availableSubhires : allSubhires.value;
  return subhiresList.map(sh => ({
    label: `${sh.name || sh.subhire_number || `SH-${sh.id}`} - ${sh.supplier?.company_name || sh.supplier?.name || 'Unknown Supplier'}`,
    value: sh.id,
    subhire_number: sh.subhire_number || `SH-${sh.id}`,
    name: sh.name,
    supplier: sh.supplier,
    status: sh.status
  }));
});

// Get existing subhires that already contain this equipment line item
const existingSubhiresForLine = computed(() => {
  const subhiresList = props.availableSubhires.length > 0 ? props.availableSubhires : allSubhires.value;
  const result = [];
  
  subhiresList.forEach(subhire => {
    const matchingLine = subhire.lines?.find(
      line => line.equipment_list_content_id === parseInt(props.equipmentListLineId)
    );
    
    if (matchingLine) {
      result.push({
        subhireId: subhire.id,
        subhireNumber: subhire.subhire_number || `SH-${subhire.id}`,
        supplierName: subhire.supplier?.company_name || subhire.supplier?.name || 'No Supplier',
        quantity: matchingLine.quantity || 0
      });
    }
  });
  
  return result;
});

// Total quantity already subhired for this line
const totalSubhiredQuantity = computed(() => {
  return existingSubhiresForLine.value.reduce((sum, item) => sum + item.quantity, 0);
});

const allSubhires = ref([]);

const loadAllSubhires = async () => {
  try {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;
    
    if (!projectId || !equipmentListId) {
      console.warn('Project ID or Equipment List ID not available');
      allSubhires.value = [];
      return;
    }
    
    allSubhires.value = await getEquipmentListSubHires(projectId, equipmentListId);
  } catch (error) {
    console.error('Error loading all subhires:', error);
    allSubhires.value = [];
  }
};


const getStatusSeverity = (status) => {
  const severityMap = {
    pending: 'warning',
    booked: 'info',
    po_raised: 'info',
    confirmed: 'success',
    received: 'success',
    returned: 'secondary',
    complete: 'success',
    cancelled: 'danger'
  };
  return severityMap[status] || 'secondary';
};




const createNewSubhire = async () => {
  isCreatingNew.value = true;
  try {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;
    
    if (!projectId || !equipmentListId) {
      throw new Error('Project ID and Equipment List ID are required');
    }
    
    // Create a new subhire with default values
    const payload = {
      name: newSubhireName.value?.trim() || null,
      supplier_id: null, // Will need to be set later
      status: 'pending',
      supplier_reference: null,
      our_po_number: null,
      rental_start_datetime: null,
      rental_end_datetime: null,
      notes: null,
      transport_required: false,
      // New transport model fields
      inbound_method: null,
      outbound_method: null,
      delivery_datetime: null,
      delivery_cost: 0,
      delivery_address_id: null,
      collection_datetime: null,
      collection_cost: 0,
      collection_address_id: null,
      transport_notes: null,
      lines: [
        {
          equipment_list_content_id: parseInt(props.equipmentListLineId),
          quantity: quantityToSubhire.value,
          unit_cost: 0,
          cost_model: 'per_day',
          tax_rate: 0,
          notes: null
        }
      ]
    };
    
    await createEquipmentListSubHire(projectId, equipmentListId, payload);
    
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'New subhire created successfully',
      life: 3000
    });
    
    // Close the modal
    emit('close');
    
    // Reload subhires list for next time
    await loadAllSubhires();
    
    // Refresh subhires tab visibility
    if (projectStore.refreshSubhiresCheck) {
      await projectStore.refreshSubhiresCheck();
    }
    
    // Refresh parent component
    if (projectStore.refreshFunction) {
      await projectStore.refreshFunction();
    }
  } catch (error) {
    console.error('Error creating new subhire:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || error.message || 'Failed to create new subhire',
      life: 3000
    });
  } finally {
    isCreatingNew.value = false;
  }
};

const addEquipmentToExistingSubhire = async () => {
  if (!selectedExistingSubhireId.value) return;
  
  isAddingToExisting.value = true;
  try {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;
    
    if (!projectId || !equipmentListId) {
      throw new Error('Project ID or Equipment List ID not found');
    }
    
    // Get the selected subhire to check if it already has this equipment line
    const subhiresList = props.availableSubhires.length > 0 ? props.availableSubhires : allSubhires.value;
    const selectedSubhire = subhiresList.find(sh => sh.id === selectedExistingSubhireId.value);
    if (!selectedSubhire) {
      throw new Error('Selected subhire not found');
    }
    
    // Check if this equipment line already exists in the subhire
    const existingLine = selectedSubhire.lines?.find(
      line => line.equipment_list_content_id === parseInt(props.equipmentListLineId)
    );
    
    if (existingLine) {
      // Update existing line
      const payload = {
        equipment_list_content_id: parseInt(props.equipmentListLineId),
        quantity: (existingLine.quantity || 0) + quantityToSubhire.value,
        unit_cost: existingLine.unit_cost || 0,
        cost_model: existingLine.cost_model || 'per_day',
        tax_rate: existingLine.tax_rate || 0,
        notes: existingLine.notes || null
      };
      
      await updateSubHireLine(projectId, equipmentListId, selectedExistingSubhireId.value, existingLine.id, payload);
    } else {
      // Add new line to existing subhire
      const payload = {
        equipment_list_content_id: parseInt(props.equipmentListLineId),
        quantity: quantityToSubhire.value,
        unit_cost: 0, // Default, user can edit later
        cost_model: 'per_day', // Default, user can edit later
        tax_rate: 0,
        notes: null
      };
      
      await addSubHireLine(projectId, equipmentListId, selectedExistingSubhireId.value, payload);
    }
    
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Equipment added to subhire successfully',
      life: 3000
    });
    
    // Reset and close
    selectedExistingSubhireId.value = null;
    emit('close');
    
    // Reload subhires list for next time
    await loadAllSubhires();
    
    if (projectStore.refreshFunction) {
      await projectStore.refreshFunction();
    }
  } catch (error) {
    console.error('Error adding equipment to existing subhire:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to add equipment to subhire',
      life: 3000
    });
  } finally {
    isAddingToExisting.value = false;
  }
};

// Watch for subhire selection and automatically add equipment
watch(selectedExistingSubhireId, (newValue) => {
  if (newValue) {
    // Automatically add equipment when a subhire is selected
    addEquipmentToExistingSubhire();
  }
});

onMounted(() => {
  loadAllSubhires();
});

</script>

<style scoped>
.manage-subhires-modal {
  padding: 1rem;
}
</style>
